import { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  Radio,
  RadioGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';

// Core components
import Form from '../../lib/appfuse-react/component/Form';
import TextField from '../../lib/appfuse-react/component/TextField';
import DateField from '../../lib/appfuse-react/component/DateField';
import SelectField from '../../lib/appfuse-react/component/SelectField';
import Button from '../../lib/appfuse-react/component/Button';
import usePrompt from '../../lib/appfuse-react/hook/usePrompt';

// Services
import candidateService from '../../service/candidate/candidateService';

// Local components
import { ClayCard, QuestionTitle, SectionNav, MobileDrawerNav } from './components';

// Constants
import {
  claymorphismColors,
  requiredFields,
  sectionConfig,
  initializedMissingFields,
  departmentList,
  citiesArray,
  degreesArray,
  statusArray,
  activityOptions,
} from './constants';

// Utils
import {
  validateFields,
  getCurrentTime,
  getActiveActivity,
  isActivitySelectable,
  getDisplayableActivities,
} from './utils';

// Styles
import { useStyles } from './styles';

function Resume(props) {
  const classes = useStyles();
  const theme = useTheme();
  const smDown = useMediaQuery(theme.breakpoints.down('sm'));
  const history = useHistory();
  const prompt = usePrompt();

  // Form state
  const [value, setValue] = useState({});
  const [change, setChange] = useState({});
  const [isButtonBusy, setIsButtonBusy] = useState(false);
  const isSubmittingRef = useRef(false); // Sync lock to prevent rapid double-clicks
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [certificate, setCertificate] = useState({
    toeicScore: false,
    toeflScore: false,
    geptScore: false,
    ieltsScore: false,
    otherScore: false,
    noEnglishScore: false,
  });
  const [hasSubmitted, setHasSubmitted] = useState(false);

  // Derived states for conditional fields
  const otherNationality = value.nationality === 'others';
  const dischargeTime = value.militaryStatus === 'inService';
  const otherSchool = value.school === 'other';
  const otherLanguage = value.language === 'yes';
  const otherActivity = value.activity === 'other';

  // Real-time validation - only active after first submit
  const computedMissingFields = useMemo(() => {
    if (!hasSubmitted) return initializedMissingFields;
    return validateFields(value, certificate, isEmailValid);
  }, [hasSubmitted, value, certificate, isEmailValid]);

  // Activity time logic - auto-select active activity
  const currentTime = useMemo(() => getCurrentTime(activityOptions), []);
  const activeActivity = useMemo(
    () => getActiveActivity(activityOptions, currentTime),
    [currentTime]
  );
  const displayableActivities = useMemo(
    () => getDisplayableActivities(activityOptions, activeActivity),
    [activeActivity]
  );

  // Auto-select active activity on mount
  useEffect(() => {
    if (activeActivity && !value.activity) {
      setValue(prev => ({ ...prev, activity: activeActivity.value }));
    }
  }, [activeActivity, value.activity]);

  // Auto-reset busy state after 15 seconds
  useEffect(() => {
    const intervalId = setInterval(() => {
      if (isButtonBusy) {
        setIsButtonBusy(false);
      }
    }, 15 * 1000);
    return () => clearInterval(intervalId);
  }, [isButtonBusy]);

  // Form change handler
  const handleChange = (event) => {
    const { value: newValue, change: newChange } = event;

    if (newChange.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      setIsEmailValid(emailRegex.test(newValue.email));
    }

    // Clear "other" fields when switching away from "other" option
    const clearOtherField = (fieldName, condition, otherFieldName) => {
      if (newChange[fieldName] && newChange[fieldName] !== condition && value[fieldName] === condition) {
        newChange[otherFieldName] = null;
        newValue[otherFieldName] = null;
      }
    };

    clearOtherField('nationality', 'others', 'otherNationality');
    clearOtherField('militaryStatus', 'inService', 'dischargeTime');
    clearOtherField('school', 'other', 'otherSchool');
    clearOtherField('priority1', 'other', 'otherPriority1');
    clearOtherField('priority2', 'other', 'otherPriority2');
    clearOtherField('language', 'yes', 'otherLanguage');
    clearOtherField('activity', 'other', 'otherActivity');

    setValue(newValue);
    setChange(newChange);
  };

  // Other field change handler
  const handleOtherFieldChange = (event, name) => {
    const { value: fieldValue } = event;
    setChange({ ...change, [name]: fieldValue });
    setValue(prev => ({ ...prev, [name]: fieldValue }));
  };

  // Certificate checkbox handler
  const handleCertificateChange = (event) => {
    setCertificate({ ...certificate, [event.target.name]: event.target.checked });
  };

  // Form submit handler
  const handleSubmit = async () => {
    // Prevent rapid double-clicks with sync lock
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsButtonBusy(true);

    setHasSubmitted(true);

    const currentMissingFields = validateFields(value, certificate, isEmailValid);
    const missingFieldNames = requiredFields.filter(field => currentMissingFields[field]);

    if (missingFieldNames.length > 0) {
      prompt.warn('尚有必填欄位未填寫，請查看填寫進度\nPlease complete all required fields');

      const firstMissingSectionId = sectionConfig.find(section =>
        section.fields.some(field => currentMissingFields[field])
      )?.id;

      if (firstMissingSectionId) {
        setTimeout(() => {
          document.getElementById(firstMissingSectionId)?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }, 100);
      }
      isSubmittingRef.current = false;
      setIsButtonBusy(false);
      return;
    }

    try {
      const isExisted = await candidateService.isCandidateExisted(value.primaryContactNumber, value.email);
      if (isExisted) {
        prompt.warn('此手機號碼或信箱已經有人使用，請重新填寫 \n this phone number or email has been used, please fill in again');
        isSubmittingRef.current = false;
        setIsButtonBusy(false);
        return;
      }
      await candidateService.post(value);
      // Keep busy state on success - user will be redirected
      prompt.success('感謝您的填寫');
      history.push('/thanks');
    } catch (e) {
      await prompt.alert(e);
      isSubmittingRef.current = false;
      setIsButtonBusy(false);
    }
  };

  // Render priority radio group
  const renderPriority = (name) => (
    <RadioGroup row name={name}>
      {renderDepartmentList(name)}
    </RadioGroup>
  );

  // Render department list with 3 columns layout
  const renderDepartmentList = (name) => {
    const FACTOR = 3;
    if (!departmentList) return null;

    const groupList = departmentList.reduce((acc, cur, index) => {
      const groupIndex = Math.floor(index / FACTOR);
      if (!acc[groupIndex]) acc[groupIndex] = [];
      acc[groupIndex].push(cur);
      return acc;
    }, []);

    const priorityMap = { priority1: 'otherPriority1', priority2: 'otherPriority2' };
    const otherPriorityName = priorityMap[name];
    const otherPriorityDisable = value[name] !== 'other';

    return groupList.map((tinyDepartmentList, index) => {
      const isLastGroup = index === groupList.length - 1;
      return (
        <Grid key={`group-${index}`} item container md={12} spacing={1}>
          {tinyDepartmentList.map(department => (
            <Grid key={department.name} item md={4} xs={12}>
              <FormControlLabel
                value={department.name}
                control={<Radio style={{ color: claymorphismColors.accent }} />}
                label={department.name}
              />
            </Grid>
          ))}
          {isLastGroup && (
            <Grid item container md={12} spacing={1}>
              <Grid item md={8} xs={12}>
                <Box width={'100%'} display={'flex'} alignItems={smDown ? 'flex-start' : 'center'} flexDirection={smDown ? 'column' : 'row'}>
                  <FormControlLabel
                    style={{ whiteSpace: 'nowrap', marginRight: '8px' }}
                    value="other"
                    control={<Radio style={{ color: claymorphismColors.accent }} />}
                    label="其他 Others"
                  />
                  <Box className={classes.clayInput} style={{ flex: 1, width: smDown ? '100%' : 'auto', marginTop: smDown ? '8px' : 0 }}>
                    <TextField
                      variant={'outlined'}
                      fullWidth
                      name={otherPriorityName}
                      label={'請填寫部門名稱'}
                      disabled={otherPriorityDisable}
                      onChange={(event) => handleOtherFieldChange(event, otherPriorityName)}
                      value={value[otherPriorityName]}
                    />
                  </Box>
                </Box>
              </Grid>
            </Grid>
          )}
        </Grid>
      );
    });
  };

  // Static text
  const thanksText = "Thank you for filling in, this resume can be completed in five minutes!";
  const remindText = "Please be sure to check whether your answers are correct before sending them out, thank you!";
  const submitText = "完成作答 Finish";

  return (
    <>
      <Form
        className={classes.form}
        value={value}
        change={change}
        onChange={handleChange}
        onSubmit={handleSubmit}
      >
        <Box className={classes.formWithNav}>
          <Box className={classes.formContent}>
            {/* Hero Section */}
            <Box className={classes.heroSection}>
              <Typography variant={'h2'} className={classes.heroTitle}>
                感謝您的填寫，本簡歷可在五分鐘內完成！
              </Typography>
              <Typography variant={'h4'} className={classes.heroSubtitle}>{thanksText}</Typography>
              <Box marginTop={2} display={'flex'} justifyContent={'center'} flexWrap={'wrap'} style={{ gap: '8px' }}>
                <Typography variant={'h5'} style={{ color: claymorphismColors.text }}>請填寫基本資料 General information</Typography>
                <Typography variant={'h5'} className={classes.requiredMark}>*為必填 *required field</Typography>
              </Box>
            </Box>

            {/* Card 1: Personal Info */}
            <ClayCard id="section-personal" colorVariant="orange" title="基本資料 Personal Information" classes={classes}>
              <Grid container spacing={3}>
                <Grid item container md={12} xs={12} spacing={1} className={classes.clayRadio}>
                  <Grid item md={12} xs={12}>
                    <QuestionTitle title={'您的姓名 Name'} required isMissing={computedMissingFields.lastName || computedMissingFields.firstName} classes={classes} />
                  </Grid>
                  <Grid item md={4} xs={12} className={classes.clayInput}><TextField variant={'outlined'} fullWidth name={'lastName'} label={'姓 Last name'} /></Grid>
                  <Grid item md={4} xs={12} className={classes.clayInput}><TextField variant={'outlined'} fullWidth name={'firstName'} label={'名 First name'} /></Grid>
                </Grid>
                <Grid item container md={12} xs={12} spacing={1} className={classes.clayRadio}>
                  <Grid item md={12} xs={12}>
                    <QuestionTitle title={'請選擇您的性別 Gender'} required isMissing={computedMissingFields.gender} classes={classes} />
                  </Grid>
                  <Grid item container md={12} xs={12}>
                    <RadioGroup style={{ width: "100%" }} row name={"gender"}>
                      <Grid item container md={12} spacing={1}>
                        <Grid item md={4} xs={12}>
                          <FormControlLabel value="male" control={<Radio style={{ color: claymorphismColors.accent }} />} label="男性 Male" />
                        </Grid>
                        <Grid item md={4} xs={12}>
                          <FormControlLabel value="female" control={<Radio style={{ color: claymorphismColors.accent }} />} label="女性 Female" />
                        </Grid>
                      </Grid>
                    </RadioGroup>
                  </Grid>
                </Grid>
                <Grid item container md={12} xs={12} spacing={1} className={classes.clayRadio}>
                  <Grid item md={12} xs={12}>
                    <QuestionTitle title={'您的國籍 Nationality'} required isMissing={computedMissingFields.nationality} classes={classes} />
                  </Grid>
                  <Grid item container md={12} xs={12}>
                    <RadioGroup style={{ width: "100%" }} row name={"nationality"}>
                      <Grid item container md={12} spacing={1}>
                        <Grid style={{ display: 'flex', alignItems: 'center' }} item md={4} xs={12}>
                          <FormControlLabel value="taiwan" control={<Radio style={{ color: claymorphismColors.accent }} />} label="本國籍 Taiwan" />
                        </Grid>
                        <Grid item md={8} xs={12}>
                          <Box display={'flex'} alignItems={smDown ? 'flex-start' : 'center'} justifyContent={'start'} flexDirection={smDown ? 'column' : 'row'}>
                            <FormControlLabel style={{ whiteSpace: 'nowrap', marginRight: '8px' }} value="others" control={<Radio style={{ color: claymorphismColors.accent }} />} label="外國籍 Others" />
                            <Box className={classes.clayInput} style={{ flex: 1, width: smDown ? '100%' : 'auto', marginTop: smDown ? '8px' : 0 }}>
                              <TextField
                                variant={'outlined'}
                                fullWidth
                                name={'otherNationality'}
                                label={'請填寫 Please fill in'}
                                disabled={!otherNationality}
                                onChange={(event) => handleOtherFieldChange(event, 'otherNationality')}
                                value={value.otherNationality}
                              />
                            </Box>
                          </Box>
                        </Grid>
                      </Grid>
                    </RadioGroup>
                  </Grid>
                </Grid>
                <Grid item container md={12} xs={12} spacing={1}>
                  <Grid item md={12} xs={12}>
                    <QuestionTitle title={'您的戶籍地址，海外人士請選擇台灣居住地區 Your residence address, for foreigners, please select your area of residence in Taiwan'} required isMissing={computedMissingFields.registered} classes={classes} />
                  </Grid>
                  <Grid item container md={4} xs={12} className={classes.clayInput}>
                    <SelectField
                      variant={'outlined'}
                      fullWidth
                      name={"registered"}
                      label={"請選擇戶籍地"}
                      options={citiesArray}
                      textAccessor={'text'}
                      valueAccessor={'value'}
                    />
                  </Grid>
                </Grid>
                <Grid item container md={12} xs={12} spacing={1}>
                  <Grid item md={12} xs={12}>
                    <QuestionTitle title={'請選擇您的出生年月日 Date of birth'} required isMissing={computedMissingFields.birthday} classes={classes} />
                  </Grid>
                  <Grid item container md={4} xs={12} className={classes.clayInput}>
                    <DateField variant={'outlined'} fullWidth name={"birthday"} label={"出生年月日"} />
                  </Grid>
                </Grid>
                <Grid item container md={12} xs={12} spacing={1}>
                  <Grid item md={12} xs={12}>
                    <QuestionTitle title={'手機號碼 Mobile phone number'} required isMissing={computedMissingFields.primaryContactNumber} classes={classes} />
                  </Grid>
                  <Grid item container md={4} xs={12} className={classes.clayInput}>
                    <TextField variant={'outlined'} fullWidth name={"primaryContactNumber"} label={"請填寫手機號碼"} />
                  </Grid>
                </Grid>
                <Grid item container md={12} xs={12} spacing={1}>
                  <Grid item md={12} xs={12}>
                    <QuestionTitle title={'信箱 E-mail'} required isMissing={computedMissingFields.email} classes={classes} />
                  </Grid>
                  <Grid item container md={4} xs={12} className={classes.clayInput}>
                    <TextField
                      variant={'outlined'}
                      fullWidth
                      name={"email"}
                      label={"請填寫E-mail"}
                      error={!isEmailValid && !!value.email}
                      helperText={isEmailValid
                        ? "若已填寫過，建議可改用其他信箱 If you have already filled this out, we suggest using another email address."
                        : "您的E-mail格式不正確 Your E-mail format is incorrect"}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </ClayCard>

            {/* Card 2: Military Status */}
            <ClayCard id="section-military" colorVariant="gold" title="兵役狀況 Military Status" classes={classes}>
              <Grid container spacing={3} className={classes.clayRadio}>
                <Grid item container md={12} xs={12} spacing={1}>
                  <Grid item md={12} xs={12}>
                    <QuestionTitle title={'兵役狀況（女性請填免役）'} subTitle={'Foreigner and female please choose exemption'} required isMissing={computedMissingFields.militaryStatus} classes={classes} />
                  </Grid>
                  <Grid item container md={12} xs={12}>
                    <RadioGroup style={{ width: "100%" }} row name={"militaryStatus"}>
                      <Grid item container md={12} spacing={1}>
                        <Grid style={{ display: 'flex', alignItems: 'center' }} item md={4} xs={12}>
                          <FormControlLabel value="exempt" control={<Radio style={{ color: claymorphismColors.accent }} />} label="免役 Exemption" />
                        </Grid>
                        <Grid style={{ display: 'flex', alignItems: 'center' }} item md={4} xs={12}>
                          <FormControlLabel value="finish" control={<Radio style={{ color: claymorphismColors.accent }} />} label="役畢 Discharged" />
                        </Grid>
                        <Grid style={{ display: 'flex', alignItems: 'center' }} item md={4} xs={12}>
                          <FormControlLabel value="notYet" control={<Radio style={{ color: claymorphismColors.accent }} />} label="待役中 Not Yet" />
                        </Grid>
                      </Grid>
                      <Grid item container md={12} spacing={1} style={{ marginTop: '8px' }}>
                        <Grid item md={8} xs={12}>
                          <Box display={'flex'} justifyContent={'start'} alignItems={smDown ? 'flex-start' : 'center'} flexDirection={smDown ? 'column' : 'row'}>
                            <FormControlLabel style={{ whiteSpace: 'nowrap', marginRight: '8px' }} value="inService" control={<Radio style={{ color: claymorphismColors.accent }} />} label="屆退 In Service" />
                            <Box className={classes.clayInput} style={{ flex: 1, width: smDown ? '100%' : 'auto', marginTop: smDown ? '8px' : 0 }}>
                              <DateField
                                variant={'outlined'}
                                fullWidth
                                name={'dischargeTime'}
                                label={'預計退伍日期（Discharge Time）'}
                                disabled={!dischargeTime}
                                onChange={(event) => handleOtherFieldChange(event, 'dischargeTime')}
                                value={value.dischargeTime}
                              />
                            </Box>
                          </Box>
                        </Grid>
                      </Grid>
                    </RadioGroup>
                  </Grid>
                </Grid>
              </Grid>
            </ClayCard>

            {/* Card 3: Education */}
            <ClayCard id="section-education" colorVariant="orange" title="學歷資訊 Education" classes={classes}>
              <Grid container spacing={3} className={classes.clayRadio}>
                <Grid item container md={12} xs={12} spacing={1}>
                  <Grid item md={12} xs={12}>
                    <QuestionTitle title={'目前身分 You are…'} required isMissing={computedMissingFields.currentStatuses} classes={classes} />
                  </Grid>
                  <Grid item container md={12} xs={12}>
                    <RadioGroup style={{ width: "100%" }} row name={"currentStatuses"}>
                      <Grid item container md={12} spacing={1}>
                        <Grid item md={4} xs={12}>
                          <FormControlLabel value="student" control={<Radio style={{ color: claymorphismColors.accent }} />} label="學生 Student" />
                        </Grid>
                        <Grid item md={4} xs={12}>
                          <FormControlLabel value="employed" control={<Radio style={{ color: claymorphismColors.accent }} />} label="就業中 Employed" />
                        </Grid>
                        <Grid item md={4} xs={12}>
                          <FormControlLabel value="betweenJobs" control={<Radio style={{ color: claymorphismColors.accent }} />} label="待業求職者 Between jobs" />
                        </Grid>
                      </Grid>
                    </RadioGroup>
                  </Grid>
                </Grid>
                <Grid item container md={12} xs={12} spacing={1}>
                  <Grid item md={12} xs={12}>
                    <QuestionTitle title={'最高學歷 Highest education level'} required isMissing={computedMissingFields.degree || computedMissingFields.educationStatus} classes={classes} />
                  </Grid>
                  <Grid item container md={4} xs={12} className={classes.clayInput}>
                    <SelectField
                      variant={'outlined'}
                      fullWidth
                      name={"degree"}
                      label={'最高學歷 Education Level'}
                      options={degreesArray}
                      textAccessor={'name'}
                      valueAccessor={'code'}
                    />
                  </Grid>
                  <Grid item container md={4} xs={12} className={classes.clayInput}>
                    <SelectField
                      variant={'outlined'}
                      fullWidth
                      name={"educationStatus"}
                      label={'最高就學狀態 Education Status'}
                      options={statusArray}
                    />
                  </Grid>
                </Grid>
                <Grid item container md={12} xs={12} spacing={1}>
                  <Grid item md={12} xs={12}>
                    <QuestionTitle title={'最高學歷就讀或畢業學校 School'} required isMissing={computedMissingFields.school} classes={classes} />
                  </Grid>
                  <Grid item container md={12} xs={12}>
                    <RadioGroup row name={"school"} style={{ width: '100%' }}>
                      <Grid item container md={12} spacing={1}>
                        <Grid item md={4} xs={12}><FormControlLabel value="台灣大學" control={<Radio style={{ color: claymorphismColors.accent }} />} label="台灣大學 NTU" /></Grid>
                        <Grid item md={4} xs={12}><FormControlLabel value="成功大學" control={<Radio style={{ color: claymorphismColors.accent }} />} label="成功大學 NCKU" /></Grid>
                        <Grid item md={4} xs={12}><FormControlLabel value="元智大學" control={<Radio style={{ color: claymorphismColors.accent }} />} label="元智大學 YZU" /></Grid>
                      </Grid>
                      <Grid item container md={12} spacing={1}>
                        <Grid item md={4} xs={12}><FormControlLabel value="淡江大學" control={<Radio style={{ color: claymorphismColors.accent }} />} label="淡江大學 TKU" /></Grid>
                        <Grid item md={4} xs={12}><FormControlLabel value="台北科技大學" control={<Radio style={{ color: claymorphismColors.accent }} />} label="台北科技大學 NTUT" /></Grid>
                        <Grid item md={4} xs={12}><FormControlLabel value="中央大學" control={<Radio style={{ color: claymorphismColors.accent }} />} label="中央大學 NCU" /></Grid>
                      </Grid>
                      <Grid item container md={12} spacing={1}>
                        <Grid item md={4} xs={12}><FormControlLabel value="宜蘭大學" control={<Radio style={{ color: claymorphismColors.accent }} />} label="宜蘭大學 NIU" /></Grid>
                        <Grid item md={4} xs={12}><FormControlLabel value="台灣科技大學" control={<Radio style={{ color: claymorphismColors.accent }} />} label="台灣科技大學 NTUST" /></Grid>
                        <Grid item md={4} xs={12}><FormControlLabel value="中原大學" control={<Radio style={{ color: claymorphismColors.accent }} />} label="中原大學 CYCU" /></Grid>
                      </Grid>
                      <Grid item container md={12} spacing={1}>
                        <Grid item md={4} xs={12}><FormControlLabel value="長榮大學" control={<Radio style={{ color: claymorphismColors.accent }} />} label="長榮大學 CJCU" /></Grid>
                        <Grid item md={4} xs={12}><FormControlLabel value="陽明交通大學" control={<Radio style={{ color: claymorphismColors.accent }} />} label="陽明交通大學 NYCU" /></Grid>
                        <Grid item md={4} xs={12}><FormControlLabel value="屏東科技大學" control={<Radio style={{ color: claymorphismColors.accent }} />} label="屏東科技大學 NPUST" /></Grid>
                      </Grid>
                      <Grid item container md={12} spacing={1}>
                        <Grid item md={8} xs={12}>
                          <Box display={'flex'} justifyContent={'start'} alignItems={smDown ? 'flex-start' : 'center'} flexDirection={smDown ? 'column' : 'row'}>
                            <FormControlLabel style={{ whiteSpace: 'nowrap', marginRight: '8px' }} value="other" control={<Radio style={{ color: claymorphismColors.accent }} />} label="其他 Others" />
                            <Box className={classes.clayInput} style={{ flex: 1, width: smDown ? '100%' : 'auto', marginTop: smDown ? '8px' : 0 }}>
                              <TextField
                                variant={'outlined'}
                                fullWidth
                                name={'otherSchool'}
                                label={'請填寫學校名稱'}
                                disabled={!otherSchool}
                                onChange={(event) => handleOtherFieldChange(event, 'otherSchool')}
                                value={value.otherSchool}
                              />
                            </Box>
                          </Box>
                        </Grid>
                      </Grid>
                    </RadioGroup>
                  </Grid>
                </Grid>
                <Grid item container md={12} xs={12} spacing={1}>
                  <Grid item md={12} xs={12}>
                    <QuestionTitle title={'最高學歷就讀或畢業科系 Major'} required isMissing={computedMissingFields.major} classes={classes} />
                  </Grid>
                  <Grid item container md={4} xs={11} className={classes.clayInput}>
                    <TextField fullWidth variant={'outlined'} name={'major'} label={"範例：英語系 e.g. English"} />
                  </Grid>
                </Grid>
              </Grid>
            </ClayCard>

            {/* Card 4: Job Preferences */}
            <ClayCard id="section-job" colorVariant="gold" title="應徵職務 Job Preferences" classes={classes}>
              <Grid container spacing={3} className={classes.clayRadio}>
                <Grid item container md={12} xs={12} spacing={1}>
                  <Grid item md={12} xs={12}>
                    <QuestionTitle title={'預計應徵職務第一順位 Priority'} required isMissing={computedMissingFields.priority1} classes={classes} />
                  </Grid>
                  <Grid item container md={12} xs={12}>
                    {renderPriority('priority1')}
                  </Grid>
                </Grid>
                <Grid item container md={12} xs={12} spacing={1}>
                  <Grid item md={12} xs={12}>
                    <QuestionTitle title={'預計應徵職務第二順位 Priority'} classes={classes} />
                  </Grid>
                  <Grid item container md={12} xs={12}>
                    {renderPriority('priority2')}
                  </Grid>
                </Grid>
              </Grid>
            </ClayCard>

            {/* Card 5: Language Skills */}
            <ClayCard id="section-language" colorVariant="orange" title="語言能力 Language Skills" classes={classes}>
              <Grid container spacing={3} className={classes.clayRadio}>
                <Grid item container md={12} xs={12} spacing={1}>
                  <Grid item md={12} xs={12}>
                    <QuestionTitle
                      title={'是否有英語相關檢定證明（可複選）'}
                      subTitle={'certificate/patent/language qualification exam(Multiple options)'}
                      required
                      isMissing={computedMissingFields.certificate}
                      classes={classes}
                    />
                  </Grid>
                  <Grid item container md={12} xs={12}>
                    <FormGroup style={{ width: '100%' }} row>
                      <Grid style={{ margin: '.5rem 0 .5rem 0' }} item container md={12} spacing={1}>
                        <Grid item md={4} xs={12}>
                          <Box width={'100%'} padding={'0 .5rem 0 0'} display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
                            <FormControlLabel value="toeic" control={<Checkbox name={'toeicScore'} style={{ color: claymorphismColors.accent }} onChange={handleCertificateChange} />} label="TOEIC多益" />
                            <Box className={classes.clayInput} style={{ flex: 1, marginLeft: '4px' }}>
                              <TextField variant={'outlined'} fullWidth name={'toeicScore'} disabled={!certificate.toeicScore} label={"分數Score"} value={value.toeicScore} onChange={(event) => handleOtherFieldChange(event, 'toeicScore')} />
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item md={4} xs={12}>
                          <Box width={'100%'} padding={'0 .5rem 0 0'} display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
                            <FormControlLabel value="toefl" control={<Checkbox name="toeflScore" style={{ color: claymorphismColors.accent }} onChange={handleCertificateChange} />} label="TOEFL托福" />
                            <Box className={classes.clayInput} style={{ flex: 1, marginLeft: '4px' }}>
                              <TextField variant={'outlined'} fullWidth name={'toeflScore'} disabled={!certificate.toeflScore} label={"分數Score"} value={value.toeflScore} onChange={(event) => handleOtherFieldChange(event, 'toeflScore')} />
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item md={4} xs={12}>
                          <Box width={'100%'} padding={'0 .5rem 0 0'} display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
                            <FormControlLabel value="gept" control={<Checkbox name={'geptScore'} style={{ color: claymorphismColors.accent }} onChange={handleCertificateChange} />} label="GEPT全民英檢" />
                            <Box className={classes.clayInput} style={{ flex: 1, marginLeft: '4px' }}>
                              <TextField variant={'outlined'} fullWidth name={'geptScore'} disabled={!certificate.geptScore} label={"分數Score"} value={value.geptScore} onChange={(event) => handleOtherFieldChange(event, 'geptScore')} />
                            </Box>
                          </Box>
                        </Grid>
                      </Grid>
                      <Grid style={{ margin: '.5rem 0 .5rem 0' }} item container md={12} spacing={1}>
                        <Grid item md={4} xs={12}>
                          <Box width={'100%'} padding={'0 .5rem 0 0'} display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
                            <FormControlLabel value="ielts" control={<Checkbox name={'ieltsScore'} style={{ color: claymorphismColors.accent }} onChange={handleCertificateChange} />} label="IELTS雅思" />
                            <Box className={classes.clayInput} style={{ flex: 1, marginLeft: '4px' }}>
                              <TextField variant={'outlined'} fullWidth name={'ieltsScore'} disabled={!certificate.ieltsScore} label={"分數Score"} value={value.ieltsScore} onChange={(event) => handleOtherFieldChange(event, 'ieltsScore')} />
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item md={4} xs={12}>
                          <Box width={'100%'} padding={'0 .5rem 0 0'} display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
                            <FormControlLabel value="others" control={<Checkbox name={'otherScore'} style={{ color: claymorphismColors.accent }} onChange={handleCertificateChange} />} label="其他others" />
                            <Box className={classes.clayInput} style={{ flex: 1, marginLeft: '4px' }}>
                              <TextField variant={'outlined'} fullWidth name={'otherScore'} disabled={!certificate.otherScore} label={"證照/專利"} value={value.otherScore} onChange={(event) => handleOtherFieldChange(event, 'otherScore')} />
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item md={4} xs={12}>
                          <Box width={'100%'} display={'flex'} alignItems={'center'} height={'100%'}>
                            <FormControlLabel value="no" control={<Checkbox name={'noEnglishScore'} style={{ color: claymorphismColors.accent }} onChange={handleCertificateChange} />} label="無 No" />
                          </Box>
                        </Grid>
                      </Grid>
                    </FormGroup>
                  </Grid>
                </Grid>
                <Grid item container md={12} xs={12} spacing={1}>
                  <Grid item md={12} xs={12}>
                    <QuestionTitle title={'是否有精通其他語言 Second Language'} required isMissing={computedMissingFields.language} classes={classes} />
                  </Grid>
                  <Grid item container md={12} xs={12}>
                    <RadioGroup style={{ width: "100%" }} row name={"language"}>
                      <Grid item container md={12} spacing={1}>
                        <Grid style={{ display: 'flex', alignItems: 'center' }} item md={4} xs={12}>
                          <FormControlLabel value="no" control={<Radio style={{ color: claymorphismColors.accent }} />} label="無 No" />
                        </Grid>
                        <Grid item md={8} xs={12}>
                          <Box width={'100%'} display={'flex'} alignItems={smDown ? 'flex-start' : 'center'} flexDirection={smDown ? 'column' : 'row'}>
                            <FormControlLabel style={{ whiteSpace: 'nowrap', marginRight: '8px' }} value="yes" control={<Radio style={{ color: claymorphismColors.accent }} />} label="是 Yes" />
                            <Box className={classes.clayInput} style={{ flex: 1, width: smDown ? '100%' : 'auto', marginTop: smDown ? '8px' : 0 }}>
                              <TextField
                                variant={'outlined'}
                                fullWidth
                                name={'otherLanguage'}
                                label={'語言 & 檢定分數'}
                                disabled={!otherLanguage}
                                onChange={(event) => handleOtherFieldChange(event, 'otherLanguage')}
                                value={value.otherLanguage}
                              />
                            </Box>
                          </Box>
                        </Grid>
                      </Grid>
                    </RadioGroup>
                  </Grid>
                </Grid>
              </Grid>
            </ClayCard>

            {/* Card 6: Work Preferences */}
            <ClayCard id="section-work" colorVariant="gold" title="工作意願 Work Preferences" classes={classes}>
              <Grid container spacing={3} className={classes.clayRadio}>
                <Grid item container md={12} xs={12} spacing={1}>
                  <Grid item md={12} xs={12}>
                    <QuestionTitle
                      title={'您是否可以接受海外派駐的工作？（如：中東、東南亞、印度…等）'}
                      subTitle={'Are you willing to work overseas? ( ex, mid-east, south-east Asia, india…etc)'}
                      required
                      isMissing={computedMissingFields.expatriate}
                      classes={classes}
                    />
                  </Grid>
                  <Grid item container md={12} xs={12}>
                    <RadioGroup style={{ width: "100%" }} row name={"expatriate"}>
                      <Grid item container md={12} spacing={1}>
                        <Grid item md={4} xs={12}>
                          <FormControlLabel value="yes" control={<Radio style={{ color: claymorphismColors.accent }} />} label="是 Yes" />
                        </Grid>
                        <Grid item md={4} xs={12}>
                          <FormControlLabel value="no" control={<Radio style={{ color: claymorphismColors.accent }} />} label="否 No" />
                        </Grid>
                      </Grid>
                    </RadioGroup>
                  </Grid>
                </Grid>
                <Grid item container md={12} xs={12} spacing={1}>
                  <Grid item md={12} xs={12}>
                    <QuestionTitle title={'最快到職上班日期'} subTitle={'Expected employment date?'} required isMissing={computedMissingFields.toWorkDay} classes={classes} />
                  </Grid>
                  <Grid item container md={4} xs={12} className={classes.clayInput}>
                    <DateField variant={'outlined'} fullWidth name={'toWorkDay'} />
                  </Grid>
                </Grid>
              </Grid>
            </ClayCard>

            {/* Card 7: Activity Source */}
            <ClayCard id="section-activity" colorVariant="cyan" title="活動來源 Activity Source" classes={classes}>
              <Grid container spacing={3} className={classes.clayRadio}>
                <Grid item container md={12} xs={12} spacing={1}>
                  <Grid item md={12} xs={12}>
                    <QuestionTitle
                      title={'請問您是在哪一場活動填寫本履歷表？'}
                      subTitle={'Where did you know this website?'}
                      required
                      isMissing={computedMissingFields.activity}
                      classes={classes}
                    />
                  </Grid>
                  <Grid item container md={12} xs={12}>
                    <RadioGroup row name={"activity"} style={{ width: '100%' }}>
                      {displayableActivities.reduce((rows, activity, index) => {
                        const rowIndex = Math.floor(index / 3);
                        if (!rows[rowIndex]) rows[rowIndex] = [];
                        rows[rowIndex].push(activity);
                        return rows;
                      }, []).map((row, rowIndex, allRows) => {
                        const isLastRow = rowIndex === allRows.length - 1;
                        const remainingSlots = 3 - row.length;
                        return (
                          <Grid key={rowIndex} item container md={12} spacing={1}>
                            {row.map(activity => {
                              const isDisabled = !isActivitySelectable(activity, activeActivity);
                              return (
                                <Grid key={activity.value} item md={4} xs={12}>
                                  <FormControlLabel
                                    value={activity.value}
                                    control={<Radio style={{ color: claymorphismColors.accent }} />}
                                    label={activity.label}
                                    disabled={isDisabled}
                                    style={isDisabled ? { opacity: 0.5 } : {}}
                                  />
                                </Grid>
                              );
                            })}
                            {isLastRow && remainingSlots > 0 && (
                              <Grid item md={4} xs={12}>
                                <Box width={'100%'} display={'flex'} alignItems={smDown ? 'flex-start' : 'center'} flexDirection={smDown ? 'column' : 'row'}>
                                  <FormControlLabel
                                    style={{ whiteSpace: 'nowrap', marginRight: '8px', ...(activeActivity ? { opacity: 0.5 } : {}) }}
                                    value="other"
                                    control={<Radio style={{ color: claymorphismColors.accent }} />}
                                    label="其他 Others"
                                    disabled={!!activeActivity}
                                  />
                                  <Box className={classes.clayInput} style={{ flex: 1, width: smDown ? '100%' : 'auto', marginTop: smDown ? '8px' : 0 }}>
                                    <TextField
                                      variant={'outlined'}
                                      fullWidth
                                      name={'otherActivity'}
                                      label={'請填寫活動來源'}
                                      disabled={!otherActivity || !!activeActivity}
                                      onChange={(event) => handleOtherFieldChange(event, 'otherActivity')}
                                      value={value.otherActivity}
                                    />
                                  </Box>
                                </Box>
                              </Grid>
                            )}
                          </Grid>
                        );
                      })}
                      {displayableActivities.length % 3 === 0 && (
                        <Grid item container md={12} spacing={1}>
                          <Grid item md={4} xs={12}>
                            <Box width={'100%'} display={'flex'} alignItems={smDown ? 'flex-start' : 'center'} flexDirection={smDown ? 'column' : 'row'}>
                              <FormControlLabel
                                style={{ whiteSpace: 'nowrap', marginRight: '8px', ...(activeActivity ? { opacity: 0.5 } : {}) }}
                                value="other"
                                control={<Radio style={{ color: claymorphismColors.accent }} />}
                                label="其他 Others"
                                disabled={!!activeActivity}
                              />
                              <Box className={classes.clayInput} style={{ flex: 1, width: smDown ? '100%' : 'auto', marginTop: smDown ? '8px' : 0 }}>
                                <TextField
                                  variant={'outlined'}
                                  fullWidth
                                  name={'otherActivity'}
                                  label={'請填寫活動來源'}
                                  disabled={!otherActivity || !!activeActivity}
                                  onChange={(event) => handleOtherFieldChange(event, 'otherActivity')}
                                  value={value.otherActivity}
                                />
                              </Box>
                            </Box>
                          </Grid>
                        </Grid>
                      )}
                    </RadioGroup>
                  </Grid>
                </Grid>
              </Grid>
            </ClayCard>

            {/* Submit Section */}
            <Box className={classes.reminderBox}>
              <Typography className={classes.reminderText}>
                貼心提醒：請務必檢視答題是否正確再送出，謝謝！
              </Typography>
              <Typography className={classes.reminderSubText}>
                {remindText}
              </Typography>
              <Button
                className={classes.submitButton}
                classes={{ label: classes.buttonLabel }}
                onClick={handleSubmit}
                disabled={isButtonBusy}
              >
                {submitText}
              </Button>
            </Box>
          </Box>

          {/* Desktop/Tablet Navigation */}
          {!smDown && (
            <SectionNav
              computedMissingFields={computedMissingFields}
              classes={classes}
              hasSubmitted={hasSubmitted}
              onSubmit={handleSubmit}
              isButtonBusy={isButtonBusy}
              value={value}
              certificate={certificate}
              isEmailValid={isEmailValid}
            />
          )}
        </Box>
      </Form>

      {/* Mobile Navigation Drawer */}
      {smDown && (
        <MobileDrawerNav
          computedMissingFields={computedMissingFields}
          classes={classes}
          hasSubmitted={hasSubmitted}
          onSubmit={handleSubmit}
          isButtonBusy={isButtonBusy}
          value={value}
          certificate={certificate}
          isEmailValid={isEmailValid}
        />
      )}
    </>
  );
}

export default Resume;
